import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X, Check, Building2 } from "lucide-react";

/**
 * Reusable searchable customer select.
 *
 * Props:
 *   - customers: [{id, name, company?, email?}]
 *   - value: string (customer_id) | null
 *   - onChange: (id: string | null) => void
 *   - placeholder?: string (default "Müşteri seç…")
 *   - required?: boolean (shows red border + hint when empty)
 *   - allowClear?: boolean (default true)
 *   - size?: "sm" | "md" (default "md")
 *   - testid?: string
 *   - className?: string
 */
export default function CustomerSelect({
  customers = [],
  value,
  onChange,
  placeholder = "Müşteri seç…",
  required = false,
  allowClear = true,
  size = "md",
  testid = "customer-select",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = customers.find((c) => c.id === value);
  const empty = !value;

  // Filter
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return customers;
    return customers.filter((c) => {
      const hay = `${c.name || ""} ${c.company || ""} ${c.email || ""}`.toLocaleLowerCase("tr");
      return hay.includes(q);
    });
  }, [customers, query]);

  // Reset highlight when filter changes
  useEffect(() => { setHighlight(0); }, [query, open]);

  // Click outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Auto-focus search when opening
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // Scroll highlight into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const select = (c) => { onChange(c.id); setOpen(false); setQuery(""); };
  const clear = (e) => { e.stopPropagation(); onChange(null); setQuery(""); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[highlight]) select(filtered[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const hClass = size === "sm" ? "h-8 text-[13px]" : "h-10 text-sm";

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full ${hClass} px-3 rounded-lg border bg-white flex items-center justify-between gap-2 transition-colors hover:border-[#CBD5E1] ${open ? "border-[#4F46E5] ring-2 ring-[#4F46E5]/15" : empty && required ? "border-[#FCA5A5]" : "border-[#E2E8F0]"}`}
        data-testid={`${testid}-trigger`}
      >
        <span className="flex-1 text-left truncate">
          {selected ? (
            <span className="flex items-center gap-2">
              <span className="font-medium text-[#0F172A]">{selected.name}</span>
              {selected.company && <span className="text-[11px] text-[#94A3B8] flex items-center gap-1"><Building2 size={9}/>{selected.company}</span>}
            </span>
          ) : (
            <span className="text-[#94A3B8]">{placeholder}</span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {allowClear && selected && (
            <span onClick={clear} className="ky-btn ky-btn-ghost ky-btn-icon !w-6 !h-6" title="Temizle" data-testid={`${testid}-clear`}><X size={11}/></span>
          )}
          <ChevronDown size={14} className={`transition-transform text-[#94A3B8] ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-[#E2E8F0] shadow-xl overflow-hidden" data-testid={`${testid}-panel`}>
          <div className="relative border-b border-[#F1F5F9]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"/>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ara: ad, şirket, e-posta…"
              className="w-full h-10 pl-9 pr-3 text-sm bg-transparent focus:outline-none"
              data-testid={`${testid}-search`}
            />
          </div>
          <div ref={listRef} className="max-h-64 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[#94A3B8]">
                {customers.length === 0 ? "Henüz müşteri kayıtlı değil" : "Eşleşen müşteri yok"}
              </div>
            ) : (
              filtered.map((c, i) => {
                const isSel = c.id === value;
                const isHl = i === highlight;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    data-idx={i}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => select(c)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${isHl ? "bg-[#EEF2FF]" : ""} ${isSel ? "text-[#4F46E5]" : "text-[#0F172A]"}`}
                    data-testid={`${testid}-option-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      {(c.company || c.email) && (
                        <div className="text-[11px] text-[#94A3B8] truncate flex items-center gap-2">
                          {c.company && <span className="flex items-center gap-1"><Building2 size={9}/>{c.company}</span>}
                          {c.email && <span className="truncate">{c.email}</span>}
                        </div>
                      )}
                    </div>
                    {isSel && <Check size={13} className="text-[#4F46E5] shrink-0"/>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
